import cx from 'classnames';
import { t } from 'i18next';
import _ from 'lodash';
import { Fragment, useMemo } from 'react';
import { Link } from '../../../../../../../shared/frontend/components/link';
import { SimpleTooltip } from '../../../../../../../shared/frontend/components/presentational/tooltip';
import { LeaderboardInterstitialRow } from '../../../../../../../shared/frontend/components/ads/leaderboardInterstitial';
import { getPathToRun } from '../../../bundles/routes';
import { GuildRank } from '../guildRank';
import { TKeystoneRunRankingsTableProps, TKeystoneTableColumns } from './types';
import { TimeStyling } from './TimeStyling';
import { KeystoneRunTimeInfo } from './KeystoneRunTimeInfo';
import { KeystoneGroupMember } from './KeystoneGroupMember';
import { getDefaultSmallColumns, hasAnyRankedGroups, shouldShowInArticleAd } from './helpers';

export function KeystoneRunRankingsTableSmall(props: TKeystoneRunRankingsTableProps) {
  const cols = useMemo((): TKeystoneTableColumns => {
    const result = props.columns ? { ...props.columns } : getDefaultSmallColumns(props);

    if (!hasAnyRankedGroups(props.groups)) {
      result.rank = false;
      result.runLink = true;
    }

    return result;
  }, [props.columns, props.dungeon, props.groups]);

  const numColumns = 1 + Object.values(cols).filter((v) => v === true).length;

  const titlePrefix = props.titleSpecifiers.join(' ');

  return (
    <table
      className={cx(
        'slds-table',
        'slds-table--bordered',
        'slds-table--striped',
        'slds-table--cell-buffer',
        'rio-rankings-table'
      )}
    >
      <thead>
        <tr>
          <th colSpan={numColumns} className="slds-text-align--center text-white slds-text-heading--small">
            {props.title ? (
              props.title
            ) : (
              <>
                {props.dungeon.name ? t(`raids:${props.dungeon.slug}.name`) : 'All Dungeons'}
                <br />
                {titlePrefix.length > 15 ? (
                  <span>
                    {titlePrefix}
                    <br />
                    Mythic+ Keystone Rankings
                  </span>
                ) : (
                  <span>{titlePrefix} Mythic+ Keystone Rankings</span>
                )}
              </>
            )}
          </th>
        </tr>
      </thead>
      <tbody>
        {props.groups.length === 0 && (
          <tr>
            <td colSpan={numColumns}>
              <div
                className="slds-p-around--large slds-text-align--center slds-align--absolute-center slds-text-heading--medium"
                style={{ minHeight: '250px', whiteSpace: 'normal' }}
              >
                {t('common:errors.empty-run-leaderboard')}
              </div>
            </td>
          </tr>
        )}

        {props.groups.map((group, index: number) => (
          <Fragment key={group.run.keystone_run_id}>
            {(() => {
              const slotIndex = shouldShowInArticleAd(
                index,
                props.groups.length,
                props.currentFeatures,
                props.siteConfig
              );
              return slotIndex !== false ? (
                <LeaderboardInterstitialRow
                  slotIndex={slotIndex}
                  seed={props.siteConfig.seed || 0}
                  colSpan={numColumns}
                />
              ) : null;
            })()}

            <tr key={group.rank} className={`faction--${group.faction}`}>
              {cols.runLink && (
                <td data-label="Run" className="slds-text-align--center">
                  <Link to={getPathToRun(group.run)}>
                    <i className="fa fa-fw fa-link" />
                  </Link>
                </td>
              )}

              {cols.rank && (
                <td data-label="Rank" className="slds-text-align--center">
                  <Link to={getPathToRun(group.run)}>
                    <GuildRank rank={group.rank} />
                  </Link>
                </td>
              )}

              {cols.dungeon && (
                <td className="slds-text-align--center">{t(`raids:${group.run.dungeon.slug}.acronym`)}</td>
              )}
              {cols.level && (
                <td data-label="Rank" className="slds-text-align--left">
                  <Link to={getPathToRun(group.run)} className="rio-realm-link">
                    <span className="slds-text-heading--small">+{group.run.mythic_level}</span>
                    <span style={{ fontSize: 6, position: 'relative', left: 4, top: '-0.3rem' }}>
                      {_.range(group.run.num_chests).map((n) => (
                        <i key={n} className="rio-spaced-element--small fa fa-star text-gold" />
                      ))}
                    </span>
                  </Link>
                </td>
              )}
              {cols.time && (
                <td className="slds-text-align--center">
                  <SimpleTooltip placement="top" tooltip={<KeystoneRunTimeInfo run={group.run} />}>
                    <span>
                      <Link to={getPathToRun(group.run)} className="rio-realm-link">
                        <TimeStyling
                          timeMs={group.run.clear_time_ms}
                          precision={cols.timePrecision}
                          includeHours={!cols.timeSkipHours}
                        />
                      </Link>
                    </span>
                  </SimpleTooltip>
                </td>
              )}
              {(cols.tank || cols.healer || cols.dps) && (
                <td colSpan={3} className="slds-cell-wrap">
                  <KeystoneGroupMember
                    teamDetailsRoute={props.teamDetailsRoute}
                    team={group.run.keystone_team_id}
                    season={group.run.season}
                    showRoleIcon={cols.roleIcons !== false}
                    {...(group.run.roster.length >= 1 ? group.run.roster[0] : {})}
                  />{' '}
                  <KeystoneGroupMember
                    teamDetailsRoute={props.teamDetailsRoute}
                    team={group.run.keystone_team_id}
                    season={group.run.season}
                    showRoleIcon={cols.roleIcons !== false}
                    {...(group.run.roster.length >= 2 ? group.run.roster[1] : {})}
                  />{' '}
                  <KeystoneGroupMember
                    teamDetailsRoute={props.teamDetailsRoute}
                    team={group.run.keystone_team_id}
                    season={group.run.season}
                    showRoleIcon={cols.roleIcons !== false}
                    {...(group.run.roster.length >= 3 ? group.run.roster[2] : {})}
                  />{' '}
                  <KeystoneGroupMember
                    teamDetailsRoute={props.teamDetailsRoute}
                    team={group.run.keystone_team_id}
                    season={group.run.season}
                    {...(group.run.roster.length >= 4 ? group.run.roster[3] : {})}
                  />{' '}
                  <KeystoneGroupMember
                    teamDetailsRoute={props.teamDetailsRoute}
                    team={group.run.keystone_team_id}
                    season={group.run.season}
                    {...(group.run.roster.length >= 5 ? group.run.roster[4] : {})}
                  />
                </td>
              )}
            </tr>
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}
